import { useState, useEffect, useCallback } from 'react';
import { Globe2, Search, Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxJurisdiction } from '@/api/tax.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

// ── Dialog ────────────────────────────────────────────────────────────────────

interface JurisdictionDialogProps {
  open: boolean;
  record: TaxJurisdiction | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY: Partial<TaxJurisdiction> = {
  jurisdictionCode: '', jurisdictionName: '', country: '',
  stateProvince: null, currencyCode: null, taxAuthorityName: null,
  effectiveFrom: new Date().toISOString().slice(0, 10),
  effectiveTo: null, remarks: null, isActive: true,
};

const JurisdictionDialog = ({ open, record, onClose, onSaved }: JurisdictionDialogProps) => {
  const [form, setForm] = useState<Partial<TaxJurisdiction>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(record ? { ...record } : { ...EMPTY });
    setError(null);
  }, [record, open]);

  if (!open) return null;

  const set = (k: keyof TaxJurisdiction, v: unknown) =>
    setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.jurisdictionCode?.trim() || !form.jurisdictionName?.trim() || !form.country?.trim() || !form.effectiveFrom) {
      setError('Code, Name, Country and Effective From are required.');
      return;
    }
    setSaving(true); setError(null);
    try {
      if (record) {
        await taxApi.updateJurisdiction(record.id, form);
      } else {
        await taxApi.createJurisdiction(form);
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {record ? 'Edit Jurisdiction' : 'New Jurisdiction'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Jurisdiction Code *</label>
              <input className={inputCls} value={form.jurisdictionCode ?? ''} onChange={e => set('jurisdictionCode', e.target.value)} maxLength={20} />
            </div>
            <div>
              <label className={labelCls}>Country (ISO 3) *</label>
              <input className={inputCls} value={form.country ?? ''} onChange={e => set('country', e.target.value.toUpperCase())} maxLength={3} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Jurisdiction Name *</label>
            <input className={inputCls} value={form.jurisdictionName ?? ''} onChange={e => set('jurisdictionName', e.target.value)} maxLength={150} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>State / Province</label>
              <input className={inputCls} value={form.stateProvince ?? ''} onChange={e => set('stateProvince', e.target.value || null)} maxLength={100} />
            </div>
            <div>
              <label className={labelCls}>Currency Code</label>
              <input className={inputCls} value={form.currencyCode ?? ''} onChange={e => set('currencyCode', e.target.value.toUpperCase() || null)} maxLength={10} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Tax Authority Name</label>
            <input className={inputCls} value={form.taxAuthorityName ?? ''} onChange={e => set('taxAuthorityName', e.target.value || null)} maxLength={150} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Effective From *</label>
              <input type="date" className={inputCls} value={form.effectiveFrom ?? ''} onChange={e => set('effectiveFrom', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Effective To</label>
              <input type="date" className={inputCls} value={form.effectiveTo ?? ''} onChange={e => set('effectiveTo', e.target.value || null)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Remarks</label>
            <textarea className="w-full px-3 py-2 rounded-md border text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
              rows={2} value={form.remarks ?? ''} onChange={e => set('remarks', e.target.value || null)} maxLength={500} />
          </div>
          {record && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={form.isActive ?? true} onChange={e => set('isActive', e.target.checked)} />
              Active
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const TaxJurisdictionsPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.TAX_JURISDICTION_CREATE as string);
  const canEdit   = hasPermission(Permissions.TAX_JURISDICTION_EDIT as string);
  const canDelete = hasPermission(Permissions.TAX_JURISDICTION_DELETE as string);

  const [items,   setItems]   = useState<TaxJurisdiction[]>([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [search,        setSearch]        = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [activeOnly,    setActiveOnly]    = useState(false);

  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editRecord,   setEditRecord]   = useState<TaxJurisdiction | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaxJurisdiction | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxApi.getJurisdictions({
        search: search || undefined,
        country: countryFilter || undefined,
        isActive: activeOnly ? true : undefined,
        pageNumber: page,
        pageSize,
      });
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, countryFilter, activeOnly, page]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => { setDialogOpen(false); setEditRecord(null); load(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await taxApi.deleteJurisdiction(deleteTarget.id);
      setDeleteTarget(null); load();
    } catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Globe2 size={20} className="text-brand-600" />
              Tax Jurisdictions
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button onClick={() => { setEditRecord(null); setDialogOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus size={15} /> New Jurisdiction
              </button>
            )}
          </div>
        </div>
        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search code or name…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <input value={countryFilter} onChange={e => { setCountryFilter(e.target.value.toUpperCase()); setPage(1); }}
            placeholder="Country ISO" maxLength={3}
            className="w-28 px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={activeOnly} onChange={e => { setActiveOnly(e.target.checked); setPage(1); }} />
            Active only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Globe2 size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No jurisdictions found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Code', 'Name', 'Country', 'State/Province', 'Tax Authority', 'Effective From', 'Effective To', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono font-semibold text-brand-700 dark:text-brand-400">{row.jurisdictionCode}</td>
                  <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200">{row.jurisdictionName}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-500 dark:text-slate-400">{row.country}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{row.stateProvince ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{row.taxAuthorityName ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtDate(row.effectiveFrom)}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 whitespace-nowrap">{row.effectiveTo ? fmtDate(row.effectiveTo) : '—'}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                   : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400')}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <button onClick={() => { setEditRecord(row); setDialogOpen(true); }}
                          className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-500 transition-colors" title="Edit">
                          <Edit2 size={13} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => setDeleteTarget(row)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors" title="Delete">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <JurisdictionDialog open={dialogOpen} record={editRecord} onClose={() => { setDialogOpen(false); setEditRecord(null); }} onSaved={handleSaved} />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Delete Jurisdiction?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">This will permanently delete <strong>{deleteTarget.jurisdictionName}</strong>. This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="h-8 px-4 text-xs rounded-md bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
                {deleting && <Spinner size="sm" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
